// ─────────────────────────────────────────────────────────
//  Settings Sub-Pages — All settings detail screens
// ─────────────────────────────────────────────────────────

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  ArrowLeft, Sun, Moon, Monitor, Check,
  ChevronDown, ChevronUp, Send, Mail,
} from "lucide-react";
import { t, getLocale, setLocale, type Locale, LOCALE_NAMES, LOCALE_FLAGS } from "@/lib/i18n";

// ── Shared back header ───────────────────────────────────
function PageHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <div className="flex items-center gap-3 p-4 border-b border-border sticky top-0 bg-background/95 backdrop-blur-sm z-10">
      <button onClick={onBack} className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center hover:bg-muted/80 transition-colors">
        <ArrowLeft size={18} />
      </button>
      <h1 className="text-lg font-bold">{title}</h1>
    </div>
  );
}

// ── Toggle Row ───────────────────────────────────────────
function ToggleRow({ label, desc, value, onChange }: {
  label: string; desc?: string; value: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between py-3">
      <div>
        <p className="text-sm font-semibold">{label}</p>
        {desc && <p className="text-[11px] text-muted-foreground">{desc}</p>}
      </div>
      <button
        onClick={() => onChange(!value)}
        className={cn(
          "w-11 h-6 rounded-full transition-all relative",
          value ? "bg-gold" : "bg-muted",
        )}
      >
        <div className={cn(
          "w-5 h-5 rounded-full bg-white shadow-sm absolute top-0.5 transition-all",
          value ? "left-[22px]" : "left-0.5",
        )} />
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  1. CHANGE PASSWORD
// ═══════════════════════════════════════════════════════════
export function ChangePasswordPage({ onBack, onSave }: {
  onBack: () => void;
  onSave: (current: string, newPass: string) => Promise<void>;
}) {
  const [current, setCurrent] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async () => {
    if (newPass !== confirm) { setError("Sifreler eslesmiyor"); return; }
    if (newPass.length < 8) { setError("Sifre en az 8 karakter olmali"); return; }
    setLoading(true); setError("");
    try {
      await onSave(current, newPass);
      setSuccess(true);
      setCurrent(""); setNewPass(""); setConfirm("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Hata olustu");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <PageHeader title={t("change_password")} onBack={onBack} />
      <div className="p-4 space-y-4">
        <Input label={t("current_password")} type="password" value={current} onChange={e => setCurrent(e.target.value)} placeholder="••••••••" />
        <Input label={t("new_password")} type="password" value={newPass} onChange={e => setNewPass(e.target.value)} placeholder="En az 8 karakter" />
        <Input label={t("confirm_password")} type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Tekrar girin" />
        {error && <p className="text-ehille-red text-sm">{error}</p>}
        {success && <p className="text-ehille-green text-sm">Sifre basariyla degistirildi!</p>}
        <Button onClick={handleSave} size="lg" className="w-full" disabled={loading || !current || !newPass || !confirm}>
          {loading ? t("loading") : t("save_changes")}
        </Button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  2. APPEARANCE / THEME
// ═══════════════════════════════════════════════════════════
export function AppearancePage({ onBack }: { onBack: () => void }) {
  const [theme, setTheme] = useState<"dark" | "light" | "system">(
    () => (localStorage.getItem("ehille_theme") as "dark" | "light" | "system") ?? "dark",
  );

  const applyTheme = (t: "dark" | "light" | "system") => {
    setTheme(t);
    localStorage.setItem("ehille_theme", t);
    const isDark = t === "dark" || (t === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
    document.documentElement.classList.toggle("dark", isDark);
  };

  const themes = [
    { id: "dark" as const, icon: Moon, label: t("dark_mode"), desc: "Koyu arka plan, goz dostu" },
    { id: "light" as const, icon: Sun, label: t("light_mode"), desc: "Aydinlik ve temiz gorunum" },
    { id: "system" as const, icon: Monitor, label: t("system_mode"), desc: "Cihaz ayarini takip et" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <PageHeader title={t("appearance")} onBack={onBack} />
      <div className="p-4 space-y-3">
        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">{t("theme")}</h3>
        {themes.map(({ id, icon: Icon, label, desc }) => (
          <Card
            key={id}
            className={cn(
              "cursor-pointer transition-all active:scale-[0.98]",
              theme === id ? "border-gold bg-gold/5" : "hover:border-muted-foreground/30",
            )}
            onClick={() => applyTheme(id)}
          >
            <CardContent className="p-4 flex items-center gap-4">
              <div className={cn(
                "w-10 h-10 rounded-xl flex items-center justify-center",
                theme === id ? "bg-gold/20" : "bg-muted",
              )}>
                <Icon size={20} className={theme === id ? "text-gold" : "text-muted-foreground"} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold">{label}</p>
                <p className="text-[11px] text-muted-foreground">{desc}</p>
              </div>
              {theme === id && (
                <div className="w-6 h-6 rounded-full bg-gold flex items-center justify-center">
                  <Check size={14} className="text-ehille-dark" />
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  3. NOTIFICATION SETTINGS
// ═══════════════════════════════════════════════════════════
export function NotificationSettingsPage({ onBack }: { onBack: () => void }) {
  const [morning, setMorning] = useState(true);
  const [evening, setEvening] = useState(true);
  const [goals, setGoals] = useState(true);
  const [quiz, setQuiz] = useState(true);
  const [sadaka, setSadaka] = useState(true);

  return (
    <div className="min-h-screen bg-background">
      <PageHeader title={t("notification_settings")} onBack={onBack} />
      <div className="p-4">
        <Card>
          <CardContent className="p-4 divide-y divide-border">
            <ToggleRow label={t("morning_reminder")} desc="Her gun 05:00'te hatirlatma" value={morning} onChange={setMorning} />
            <ToggleRow label={t("evening_reminder")} desc="Her gun 20:00'de hatirlatma" value={evening} onChange={setEvening} />
            <ToggleRow label={t("goal_reminders")} desc="Tamamlanmamis hedefler icin" value={goals} onChange={setGoals} />
            <ToggleRow label={t("quiz_notifications")} desc="Yeni sinav hazirlandi" value={quiz} onChange={setQuiz} />
            <ToggleRow label={t("sadaka_alerts")} desc="Sadaka borcu uyarilari" value={sadaka} onChange={setSadaka} />
          </CardContent>
        </Card>
        <p className="text-[11px] text-muted-foreground text-center mt-4">
          Bildirim ayarlari cihazinizda yerel olarak saklanir.
        </p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  4. LANGUAGE SELECTOR
// ═══════════════════════════════════════════════════════════
export function LanguagePage({ onBack, onLocaleChange }: {
  onBack: () => void;
  onLocaleChange: (locale: Locale) => void;
}) {
  const [selected, setSelected] = useState<Locale>(getLocale());

  const handleSelect = (locale: Locale) => {
    setSelected(locale);
    setLocale(locale);
    onLocaleChange(locale);
  };

  return (
    <div className="min-h-screen bg-background">
      <PageHeader title={t("language")} onBack={onBack} />
      <div className="p-4 space-y-3">
        {(["tr", "en", "ar"] as Locale[]).map(locale => (
          <Card
            key={locale}
            className={cn(
              "cursor-pointer transition-all active:scale-[0.98]",
              selected === locale ? "border-gold bg-gold/5" : "hover:border-muted-foreground/30",
            )}
            onClick={() => handleSelect(locale)}
          >
            <CardContent className="p-4 flex items-center gap-4">
              <span className="text-2xl">{LOCALE_FLAGS[locale]}</span>
              <div className="flex-1">
                <p className="text-sm font-semibold">{LOCALE_NAMES[locale]}</p>
              </div>
              {selected === locale && (
                <div className="w-6 h-6 rounded-full bg-gold flex items-center justify-center">
                  <Check size={14} className="text-ehille-dark" />
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  5. TERMS OF USE
// ═══════════════════════════════════════════════════════════
export function TermsPage({ onBack }: { onBack: () => void }) {
  return (
    <div className="min-h-screen bg-background">
      <PageHeader title={t("terms_title")} onBack={onBack} />
      <div className="p-4 space-y-4">
        <p className="text-[11px] text-muted-foreground">{t("last_updated")}: 12 Nisan 2026</p>

        <Section title="1. Genel Hukumler">
          Ehille uygulamasini ("Uygulama") kullanimla birlikte asagidaki kosullari kabul etmis sayilirsiniz. Bu kosullar, Uygulama ile ilgili tum hak ve yukumlulukleri duzenler.
        </Section>
        <Section title="2. Hizmet Tanimi">
          Ehille, kullanicilarin gunluk hedeflerini takip etmelerine, gruplar olusturmalarina, haftalik sinavlara katilmalarina ve sadaka sistemi ile ozkontrollerini gelistirmelerine yardimci olan bir platformdur.
        </Section>
        <Section title="3. Hesap Sorumlulugu">
          Hesabinizin guvenliginden siz sorumlusunuz. Sifrenizi baskasina vermeyiniz. Hesabiniz uzerinden yapilan tum islemler sizin sorumlulugunuzdadir.
        </Section>
        <Section title="4. Kullanim Kurallari">
          Uygulamayi yasalara aykiri sekilde kullanamazsiniz. Diger kullanicilara saygisizlik, spam veya zararli icerik paylasimi yasaktir.
        </Section>
        <Section title="5. Sadaka Sistemi">
          Sadaka borcu sistemi tamamen gonullu bir ozkontrol mekanizmasidir. Uygulama herhangi bir finansal islem yapmaz. Sadaka odemeleri kullanicinin kendi insiyatifindedir.
        </Section>
        <Section title="6. Fikri Mulkiyet">
          Uygulama, logo, tasarim ve iceriklerin tum haklari Ehille ekibine aittir. Izinsiz kopyalama, dagitim veya degisiklik yapmak yasaktir.
        </Section>
        <Section title="7. Sorumluluk Siniri">
          Uygulama "oldugu gibi" sunulmaktadir. Teknik aksakliklar, veri kayiplari veya kesintiler icin sorumluluk kabul edilmez.
        </Section>
        <Section title="8. Degisiklikler">
          Bu kosullar onceden haber verilmeksizin degistirilebilir. Degisiklikler yayinlandigi anda yururluge girer.
        </Section>
        <Section title="9. Iletisim">
          Sorulariniz icin: destek@ehille.org
        </Section>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  6. PRIVACY POLICY
// ═══════════════════════════════════════════════════════════
export function PrivacyPage({ onBack }: { onBack: () => void }) {
  return (
    <div className="min-h-screen bg-background">
      <PageHeader title={t("privacy_title")} onBack={onBack} />
      <div className="p-4 space-y-4">
        <p className="text-[11px] text-muted-foreground">{t("last_updated")}: 12 Nisan 2026</p>

        <Section title="1. Toplanan Veriler">
          Hesap bilgileri (ad, e-posta), hedef tamamlama verileri, sinav sonuclari ve grup uyelik bilgileri toplanmaktadir.
        </Section>
        <Section title="2. Verilerin Kullanimi">
          Verileriniz yalnizca uygulama islevselligini saglamak, skor tablosu ve istatistikleri hesaplamak ve uygulama deneyimini iyilestirmek icin kullanilir.
        </Section>
        <Section title="3. Veri Guvenligi">
          Tum veriler Cloudflare'in kuresel ag altyapisinda sifrelenmus sekilde saklanir. Sifreleriniz hash algoritmasiyla korunur ve hicbir zaman duz metin olarak saklanmaz.
        </Section>
        <Section title="4. Ucuncu Taraflar">
          Verileriniz ucuncu taraflarla paylasilmaz. Google giris yaptiginizda yalnizca ad ve e-posta bilgisi alinir.
        </Section>
        <Section title="5. Cerezler ve Yerel Depolama">
          Uygulama, oturum yonetimi icin JWT tokenlari ve tercih ayarlari icin yerel depolama (localStorage) kullanir.
        </Section>
        <Section title="6. Veri Silme">
          Hesabinizi silmek istediginizde tum verileriniz kalici olarak silinir. Bunun icin destek@ehille.org adresine yazabilirsiniz.
        </Section>
        <Section title="7. Cocuklarin Gizliligi">
          Uygulama 13 yasindan kucuk cocuklara yonelik degildir. Bu yas grubundan bilerek veri toplamayiz.
        </Section>
        <Section title="8. Iletisim">
          Gizlilik ile ilgili sorulariniz icin: destek@ehille.org
        </Section>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  7. HELP / FAQ
// ═══════════════════════════════════════════════════════════
export function HelpPage({ onBack }: { onBack: () => void }) {
  const faqs = [
    { q: t("faq_q1"), a: t("faq_a1") },
    { q: t("faq_q2"), a: t("faq_a2") },
    { q: t("faq_q3"), a: t("faq_a3") },
    { q: t("faq_q4"), a: t("faq_a4") },
    { q: t("faq_q5"), a: t("faq_a5") },
  ];

  return (
    <div className="min-h-screen bg-background">
      <PageHeader title={t("help")} onBack={onBack} />
      <div className="p-4 space-y-4">
        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">{t("faq")}</h3>
        <div className="space-y-2">
          {faqs.map((faq, i) => <FAQItem key={i} question={faq.q} answer={faq.a} />)}
        </div>

        <Card className="mt-6">
          <CardContent className="p-4 text-center">
            <Mail size={24} className="text-gold mx-auto mb-2" />
            <p className="font-bold text-sm">{t("contact_us")}</p>
            <p className="text-muted-foreground text-xs mt-1">destek@ehille.org</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <Card className="cursor-pointer" onClick={() => setOpen(!open)}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold flex-1 pr-2">{question}</p>
          {open ? <ChevronUp size={16} className="text-muted-foreground shrink-0" /> : <ChevronDown size={16} className="text-muted-foreground shrink-0" />}
        </div>
        {open && <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{answer}</p>}
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════
//  8. FEEDBACK
// ═══════════════════════════════════════════════════════════
export function FeedbackPage({ onBack, onSubmit }: {
  onBack: () => void;
  onSubmit: (category: string, text: string) => Promise<{ issueUrl?: string }>;
}) {
  const [text, setText] = useState("");
  const [sent, setSent] = useState(false);
  const [issueUrl, setIssueUrl] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [category, setCategory] = useState<"oneri" | "hata" | "genel">("genel");

  const categories = [
    { id: "genel" as const, label: "Genel", emoji: "💬" },
    { id: "oneri" as const, label: "Oneri", emoji: "💡" },
    { id: "hata" as const, label: "Hata Bildirimi", emoji: "🐛" },
  ];

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const result = await onSubmit(category, text);
      setSent(true);
      if (result.issueUrl) setIssueUrl(result.issueUrl);
      setText("");
    } catch {
      // Error handling
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PageHeader title={t("feedback")} onBack={onBack} />
      <div className="p-4 space-y-4">
        <div className="flex gap-2">
          {categories.map(c => (
            <button
              key={c.id}
              onClick={() => setCategory(c.id)}
              className={cn(
                "flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all flex items-center justify-center gap-1.5",
                category === c.id ? "bg-gold text-ehille-dark" : "bg-muted text-muted-foreground",
              )}
            >
              <span>{c.emoji}</span> {c.label}
            </button>
          ))}
        </div>

        <textarea
          value={text}
          onChange={e => { setText(e.target.value); setSent(false); }}
          placeholder={t("feedback_placeholder")}
          rows={6}
          className="w-full rounded-xl border border-input bg-background px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
        />

        {sent && (
          <div className="text-center space-y-2">
            <p className="text-ehille-green text-sm font-medium">{t("sent_success")}</p>
            {issueUrl && (
              <a href={issueUrl} target="_blank" rel="noopener noreferrer"
                className="text-gold text-xs underline">
                GitHub Issue goruntulle →
              </a>
            )}
          </div>
        )}

        <Button onClick={handleSend} size="lg" className="w-full gap-2" disabled={!text.trim() || sending}>
          <Send size={16} /> {sending ? t("loading") : t("send_feedback")}
        </Button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  9. PROFILE EDIT
// ═══════════════════════════════════════════════════════════
export function ProfileEditPage({ name, email, onBack, onSave }: {
  name: string;
  email: string;
  onBack: () => void;
  onSave: (fields: { name?: string; bio?: string; avatarUrl?: string }) => Promise<void>;
}) {
  const [nameVal, setNameVal] = useState(name);
  const [bio, setBio] = useState(() => localStorage.getItem("ehille_bio") ?? "");
  const [avatarUrl, setAvatarUrl] = useState(() => localStorage.getItem("ehille_avatar") ?? "");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async () => {
    if (!nameVal.trim()) { setError("Ad bos olamaz"); return; }
    setLoading(true); setError(""); setSuccess(false);
    try {
      await onSave({
        name: nameVal.trim(),
        bio: bio.trim() || undefined,
        avatarUrl: avatarUrl.trim() || undefined,
      });
      // Persist bio and avatar locally as fallback
      localStorage.setItem("ehille_bio", bio.trim());
      localStorage.setItem("ehille_avatar", avatarUrl.trim());
      setSuccess(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Hata olustu");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <PageHeader title="Profil Duzenle" onBack={onBack} />
      <div className="p-4 space-y-5">
        {/* Avatar preview */}
        <div className="flex flex-col items-center gap-3">
          <div className="w-20 h-20 rounded-2xl bg-gold/20 flex items-center justify-center text-3xl font-bold text-gold overflow-hidden">
            {avatarUrl.trim() ? (
              <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
            ) : (
              nameVal.charAt(0).toUpperCase()
            )}
          </div>
          <p className="text-xs text-muted-foreground">{email}</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Ad</label>
            <Input
              value={nameVal}
              onChange={e => { setNameVal(e.target.value); setSuccess(false); }}
              placeholder="Adinizi girin"
              className="h-11 rounded-xl"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Bio</label>
            <textarea
              value={bio}
              onChange={e => { setBio(e.target.value); setSuccess(false); }}
              placeholder="Kendinizi kisaca tanitin..."
              rows={3}
              className="w-full rounded-xl border border-input bg-background px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Avatar URL</label>
            <Input
              value={avatarUrl}
              onChange={e => { setAvatarUrl(e.target.value); setSuccess(false); }}
              placeholder="https://example.com/avatar.jpg"
              className="h-11 rounded-xl"
              type="url"
            />
            <p className="text-[10px] text-muted-foreground mt-1">Profil resminiz icin bir URL girin</p>
          </div>
        </div>

        {error && <p className="text-ehille-red text-sm">{error}</p>}
        {success && <p className="text-ehille-green text-sm">Profil basariyla guncellendi!</p>}

        <Button onClick={handleSave} size="lg" className="w-full" disabled={loading || !nameVal.trim()}>
          {loading ? t("loading") : t("save_changes")}
        </Button>
      </div>
    </div>
  );
}

// ── Helper ───────────────────────────────────────────────
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground leading-relaxed">{children}</p>
      </CardContent>
    </Card>
  );
}
